import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';

const AdminQuickPaymentModal = ({ onClose, showMessage }) => {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [manualName, setManualName] = useState('');
    const [manualPhone, setManualPhone] = useState('');
    
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('Custom Payment');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [useExistingUser, setUseExistingUser] = useState(true);
    const [successOrder, setSuccessOrder] = useState(null);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const data = await callApi('/admin/users');
                setUsers(data || []);
            } catch (e) {
                console.error('Failed to fetch users', e);
            }
        };
        fetchUsers();
    }, []);

    const selectedUser = users.find(u => u._id === selectedUserId);

    const handleSubmit = async () => {
        const customerName = useExistingUser ? (selectedUser?.name || '') : manualName;
        const customerPhone = useExistingUser ? (selectedUser?.mobile || '') : manualPhone;

        if (!customerName || !customerPhone) {
            showMessage('Customer name and phone are required.', 'error');
            return;
        }
        
        if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
            showMessage('Please enter a valid amount.', 'error');
            return;
        }

        setIsSubmitting(true);

        let finalUserId = useExistingUser ? selectedUserId || null : null;

        if (!useExistingUser) {
            try {
                const cleanMobile = manualPhone.trim().replace(/\D/g, '').slice(-10);
                const alreadyExists = users.some(u => {
                    const uPhone = (u.mobile || '').replace(/\D/g, '').slice(-10);
                    return uPhone === cleanMobile;
                });

                if (!alreadyExists && cleanMobile.length === 10) {
                    const newUserRes = await callApi('/admin/users/create-customer', 'POST', {
                        name: manualName.trim(),
                        mobile: cleanMobile,
                        address: ''
                    });
                    if (newUserRes && newUserRes.user && newUserRes.user._id) {
                        finalUserId = newUserRes.user._id;
                    }
                }
            } catch (custErr) {
                console.log("Customer save handled:", custErr);
            }
        }

        const numericAmount = parseFloat(amount);
        const finalReason = reason.trim() || 'Custom Payment';

        try {
            const response = await callApi('/orders', 'POST', {
                userId: finalUserId,
                items: [{
                    productId: `custom-${Date.now()}`,
                    name: finalReason,
                    price: numericAmount,
                    quantity: 1,
                    isCustom: true
                }],
                subTotal: numericAmount,
                total: numericAmount,
                deliveryMethod: 'Store Pickup',
                customerName,
                customerPhone,
                isManualBill: true,
            });
            if (response.success || response._id || response.orderId) {
                showMessage('Payment link created successfully!', 'success');
                setSuccessOrder({
                    orderId: response.order?.orderId || response.order?._id || response.orderId || response._id,
                    total: numericAmount,
                    customerName,
                    customerPhone: customerPhone.startsWith('+') ? customerPhone : `+91${customerPhone.replace(/\D/g, '').slice(-10)}`,
                    reason: finalReason
                });
            } else {
                showMessage(response.message || 'Failed to create payment link.', 'error');
            }
        } catch (e) {
            showMessage('Error creating payment link', 'error');
            console.error(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendAction = (method) => {
        if (!successOrder) return;
        const link = `${window.location.origin}/#pay/${successOrder.orderId}`;
        const msg = `Hi ${successOrder.customerName}, please pay ₹${successOrder.total.toFixed(2)} for ${successOrder.reason}. Secure payment link: ${link}`;

        if (method === 'whatsapp') {
            window.open(`https://wa.me/${successOrder.customerPhone.replace('+', '')}?text=${encodeURIComponent(msg)}`, '_blank');
        } else if (method === 'sms') {
            window.open(`sms:${successOrder.customerPhone}?body=${encodeURIComponent(msg)}`, '_blank');
        }
    };

    if (successOrder) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden border border-slate-100">
                    <div className="bg-sky-50 px-6 py-5 border-b border-sky-100 flex justify-between items-center text-sky-800">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center text-xl font-bold shadow-md">
                                <i className="fas fa-check"></i>
                            </div>
                            <div>
                                <h3 className="text-xl font-black">Link Ready!</h3>
                                <p className="text-xs font-semibold opacity-70">Share payment link instantly</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-sky-400 hover:text-sky-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-sky-100 transition-colors">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="p-6">
                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 mb-6">
                            <div className="flex justify-between items-end mb-2">
                                <div>
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customer</p>
                                    <p className="text-sm font-bold text-slate-800">{successOrder.customerName}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Amount</p>
                                    <p className="text-lg font-black text-sky-600">₹{successOrder.total.toFixed(2)}</p>
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 italic mt-1 border-t border-slate-200 pt-2 text-center break-words">{`${window.location.origin}/#pay/${successOrder.orderId}`}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => handleSendAction('whatsapp')} className="bg-[#25D366] hover:bg-[#128C7E] text-white py-3 rounded-xl font-bold shadow-md shadow-[#25D366]/20 transition-all flex items-center justify-center gap-2">
                                <i className="fab fa-whatsapp text-lg"></i> WhatsApp
                            </button>
                            <button onClick={() => handleSendAction('sms')} className="bg-sky-500 hover:bg-sky-600 text-white py-3 rounded-xl font-bold shadow-md shadow-sky-500/20 transition-all flex items-center justify-center gap-2">
                                <i className="fas fa-comment text-lg"></i> SMS
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden max-h-[90vh]">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center text-slate-800 shrink-0">
                    <h2 className="text-lg font-black flex items-center gap-2">
                        <i className="fas fa-bolt text-sky-500"></i> Quick Payment Link
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <div className="mb-6 bg-slate-50 p-1 rounded-xl border border-slate-200 flex">
                        <button onClick={() => setUseExistingUser(true)} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${useExistingUser ? 'bg-white text-sky-600 shadow-sm border border-slate-100' : 'text-slate-500 hover:text-slate-700'}`}>
                            Existing Customer
                        </button>
                        <button onClick={() => setUseExistingUser(false)} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${!useExistingUser ? 'bg-white text-sky-600 shadow-sm border border-slate-100' : 'text-slate-500 hover:text-slate-700'}`}>
                            New Walk-in
                        </button>
                    </div>

                    <div className="space-y-4 mb-6">
                        {useExistingUser ? (
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Select Customer</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-sky-500/50 font-medium appearance-none"
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                >
                                    <option value="">-- Choose a customer --</option>
                                    {users.filter(u => u.role !== 'admin' && u.role !== 'super_admin').map(u => (
                                        <option key={u._id} value={u._id}>{u.name} ({u.mobile})</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Customer Name</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-sky-500/50 font-medium"
                                        value={manualName} 
                                        onChange={e => setManualName(e.target.value)} 
                                        placeholder="e.g. Rahul"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Mobile Number</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-sky-500/50 font-medium"
                                        value={manualPhone} 
                                        onChange={e => setManualPhone(e.target.value)} 
                                        placeholder="10 digit number"
                                        maxLength={10}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-4 pt-4 border-t border-slate-100">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Amount (₹)</label>
                            <input 
                                type="number" 
                                className="w-full bg-sky-50/50 border border-sky-200 text-sky-800 text-lg rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-sky-500 font-bold placeholder-sky-300"
                                value={amount} 
                                onChange={e => setAmount(e.target.value)} 
                                placeholder="0.00"
                                min="1"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Reason / Note</label>
                            <input 
                                type="text" 
                                className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400 font-medium"
                                value={reason} 
                                onChange={e => setReason(e.target.value)} 
                                placeholder="e.g. Debt clearance, Advance payment"
                            />
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-100 px-6 py-5 flex justify-end gap-3 bg-slate-50 shrink-0">
                    <button onClick={onClose} className="px-6 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-white transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-6 py-2.5 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-colors shadow-md disabled:opacity-70 flex items-center gap-2"
                    >
                        {isSubmitting ? <><i className="fas fa-circle-notch fa-spin"></i> Generating...</> : <><i className="fas fa-link"></i> Generate Link</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminQuickPaymentModal;
